import { useCallback } from 'react';
import { useAuthContext } from '../auth/useAuthContext';
import { useToast } from '../useToast';
import usePreAuthAction, { PreAuthAction } from '../usePreAuthAction';
import { useEditorDownloadFile } from './useEditorDownloadFile';
import { getCleanText } from '../../utilities/helpers/editor';
import { dataLayerGA } from '../../services/analytics/dataLayerGa';
import Popup from '../../services/popup/popup';
import { PopupType } from '../../utilities/constants/popup-type';
import { Phrases } from '../../utilities/constants/phrases';
import { LoginEventPopupFlow } from '../../utilities/constants/common';

export const useEditorDownloadCopyText = (text: string) => {
  const { userId } = useAuthContext();

  const { open } = useToast();
  const { setPreAuthAction } = usePreAuthAction();

  const { handleDownloadPdfFile } = useEditorDownloadFile(text);

  const onCopyText = useCallback(() => {
    dataLayerGA.copyTextClick();

    if (!userId) {
      setPreAuthAction(PreAuthAction.COPY_TEXT_ACTION);
      Popup.show(PopupType.LOGIN, { flow: LoginEventPopupFlow.OTHER });
      return;
    }

    if (navigator?.clipboard) {
      const cleanText = getCleanText(text);
      navigator.clipboard.writeText(cleanText);
      open(Phrases.TEXT_COPIED_TO_CLIPBOARD);
    }
  }, [text, open, userId, setPreAuthAction]);

  const onDownloadText = useCallback(() => {
    if (!userId) {
      setPreAuthAction(PreAuthAction.DOWNLOAD_TEXT_ACTION);
      Popup.show(PopupType.LOGIN, { flow: LoginEventPopupFlow.OTHER });
      return;
    }

    handleDownloadPdfFile();
  }, [userId, handleDownloadPdfFile, setPreAuthAction]);

  return {
    onCopyText,
    onDownloadText,
  };
};
